import React, {useRef, useState, useEffect} from "react";
import {Box, IconButton, Stack} from "@mui/material";
import {IconPlayerPlay, IconPlayerPause} from "@tabler/icons-react";

const CompactAudioPlayer = ({src}) => {
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0); // 0-100
    const [duration, setDuration] = useState(0);

    const formatDuration = (seconds) => {
        if (!seconds || !isFinite(seconds) || isNaN(seconds)) return "0s";
        const total = Math.round(seconds);
        const mins = Math.floor(total / 60);
        const secs = total % 60;
        return `${mins}:${secs.toString().padStart(2, "0")}`;
    };

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const onTimeUpdate = () => {
            if (audio.duration) {
                setProgress((audio.currentTime / audio.duration) * 100);
            }
        };
        const onLoadedMetadata = () => setDuration(audio.duration);
        const onEnded = () => {
            setIsPlaying(false);
            setProgress(0);
        };

        audio.addEventListener("timeupdate", onTimeUpdate);
        audio.addEventListener("loadedmetadata", onLoadedMetadata);
        audio.addEventListener("ended", onEnded);

        return () => {
            audio.removeEventListener("timeupdate", onTimeUpdate);
            audio.removeEventListener("loadedmetadata", onLoadedMetadata);
            audio.removeEventListener("ended", onEnded);
        };
    }, [src]);

    const togglePlay = () => {
        const audio = audioRef.current;
        if (!audio) return;
        if (isPlaying) {
            audio.pause();
        } else {
            audio.play();
        }
        setIsPlaying(!isPlaying);
    };

    const handleSeek = (e) => {
        const audio = audioRef.current;
        if (!audio || !duration) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const newProgress = (clickX / rect.width) * 100;
        audio.currentTime = (newProgress / 100) * duration;
        setProgress(newProgress);
    };

    return (
        <Stack direction="row" alignItems="center" gap={0.75} sx={{width: 110}}>
            <audio ref={audioRef} src={src} preload="metadata" style={{display: "none"}}/>

            <IconButton size="small" onClick={(e) => {
                e.stopPropagation();
                togglePlay();
            }} sx={{p: 0.25}}>
                {isPlaying ? <IconPlayerPause size={16}/> : <IconPlayerPlay size={16}/>}
            </IconButton>

            <Box
                onClick={handleSeek}
                sx={{
                    position: "relative",
                    flex: 1,
                    height: 4,
                    borderRadius: 2,
                    bgcolor: "grey.300",
                    cursor: "pointer",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        height: "100%",
                        width: `${progress}%`,
                        borderRadius: 2,
                        bgcolor: "primary.main",
                    }}
                />
            </Box>
            <Box component="span" sx={{fontSize: 11, color: "text.secondary", minWidth: 32, textAlign: "right"}}>
                {formatDuration(duration)}
            </Box>
        </Stack>
    );
};

export default CompactAudioPlayer;